import { Component, OnInit } from '@angular/core';
import { AppInfoService } from 'core/app-info/app-info.service';
import { AppStateService } from 'core/app-state/app-state.service';
import { AppState } from 'core/common-entities/app-state.class';
import { DialogData } from 'core/dialog/dialog-data.class';
import { DialogService } from 'core/dialog/dialog.service';
import { OfflineService } from '../offline-service';
import { AppLocalization } from '../entities/app-localization';

@Component({
  selector: 'app-offline-progress-bar',
  templateUrl: './offline-progress-bar.component.html',
  styleUrls: ['./offline-progress-bar.component.scss'],
  standalone: false
})
export class OfflineProgressBarComponent implements OnInit {
  private maxDaysWithoutSync: number;
  private skews = {
    withDeg: 'skew(-30deg)',
    withoutDeg: 'skew(0deg)'
  };

  progressBarValue: number;
  appStrings: AppLocalization;
  expirationMessage: string;
  daysUntilSyncNeeded: number;
  daysAgoWentOffline: number;
  mode = 'determinate';
  syncStatus = {
    required: false,
    optional: false,
    upToDate: false,
    failed: false
  };
  state: AppState;
  progressBarWidth = {
    left: '0px',
    right: '0px',
    leftSkew: this.skews.withDeg
  };

  constructor(
    private appStateService: AppStateService,
    private appInfoService: AppInfoService,
    private dialogService: DialogService,
    private offlineService: OfflineService
  ) {
  }

  ngOnInit() {
    this.appStrings = this.appInfoService.appStrings;
    this.state = this.appStateService.state;
    this.maxDaysWithoutSync = this.state.maxDaysWithoutSync;
    this.appStateService.offlineTimerSyncTriggered$.subscribe(hasChanges => {
      if (hasChanges) {
        this.calculateDaysUntilSyncNeeded();
        this.buildExpirationMessage(this.appStateService.state.synchronizationStatus);
      }
    });
  }

  /**
   * Build the message displayed on progress bar
   */
  private buildExpirationMessage(syncStatus) {
    const options = {
      0: () => {
        this.syncStatus.upToDate = true;
      },
      1: () => this.expirationMessage = this.appStrings.goOffline_syncedWithLatestData, // up to date
      2: () => { // optional
        this.displayGoOnlineModal();
        let dayLabel = this.appStrings.label_days;
        if (this.daysUntilSyncNeeded === 1) {
          dayLabel = this.appStrings.label_day;
        }
        this.expirationMessage = `${this.daysUntilSyncNeeded} ${dayLabel} ${this.appStrings.goOffline_daysUntilSyncNeededMessage}`;
      },
      3: () => this.handleRequiredOrFailedSyncStatusExpirationMessage(), // required
      4: () => this.handleRequiredOrFailedSyncStatusExpirationMessage(), // failed
    };

    const action = options[syncStatus];
    action();
  }

  /**
   * Build the expiration message and display a dialog if sync status is failed or required
   */
  private handleRequiredOrFailedSyncStatusExpirationMessage() {
    this.expirationMessage = this.appStrings.goOffline_refreshRequiredProgressBarMessage;
    this.displayInternetConnectionRequiredModal();
  }

  /**
   * Displays a modal to warn the user of number of days since the app is used in offline mode
   */
  private displayGoOnlineModal() {
    let displayedMessage = this.appStrings.goOffline_refreshDataMessage.replace('{1}', this.daysAgoWentOffline?.toString());

    if (this.daysAgoWentOffline === 1) {
      displayedMessage = displayedMessage.replace('{2}', this.appStrings.label_day);
    } else {
      displayedMessage = displayedMessage.replace('{2}', this.appStrings.label_days);
    }

    const dialogParams: DialogData = {
      title: this.appStrings.goOffline_refreshDataTitle,
      content: displayedMessage,
      buttons: [
        { label: this.appStrings.button_cancel, style: 'primary leave-button' },
        { label: this.appStrings.goOfflineDashboard_goOnlineButton, focused: true }
      ]
    };

    const dialogRef = this.dialogService.openDialog(dialogParams);
    dialogRef.afterClosed().subscribe(dialogAnswer => {
      if (dialogAnswer === 1) { // goOnline clicked
        this.tryGoOnline();
      }
    });
  }

  /**
   * Displays a modal to inform the user that a refresh of data is required to use the app
   */
  private displayInternetConnectionRequiredModal() {
    const displayedMessage = this.appStrings.goOffline_refreshRequired;
    const dialogParams: DialogData = {
      title: this.appStrings.goOffline_refreshDataTitle,
      content: displayedMessage,
      buttons: [
        { label: this.appStrings.button_cancel, style: 'leave-button' },
        {
          label: this.appStrings.goOfflineDashboard_synchronizeButton, style: 'primary',
          focused: true,
          icon: 'icon-refresh'
        }
      ]
    };

    const dialogRef = this.dialogService.openDialog(dialogParams);
    dialogRef.afterClosed().subscribe(dialogAnswer => {
      if (dialogAnswer === 1) { // goOnline clicked
        this.tryGoOnline();
      }
    });
  }

  /**
   * Get the days until a sync is required
   */
  private calculateDaysUntilSyncNeeded() {
    this.syncStatus.upToDate = this.appStateService.state.synchronizationStatus === 1;
    this.syncStatus.optional = this.appStateService.state.synchronizationStatus === 2;
    this.syncStatus.required = this.appStateService.state.synchronizationStatus === 3;
    this.syncStatus.failed = this.appStateService.state.synchronizationStatus === 4;

    this.daysAgoWentOffline = this.appStateService.getDifferenceBetweenGoOfflineAndToday();
    this.daysUntilSyncNeeded = this.maxDaysWithoutSync - this.daysAgoWentOffline;
    this.progressBarValue = (this.maxDaysWithoutSync - this.daysUntilSyncNeeded) * (100 / this.maxDaysWithoutSync);

    if (this.daysUntilSyncNeeded <= 0 || this.syncStatus.failed) {
      this.progressBarWidth = {
        left: '120%',
        right: '0%',
        leftSkew: this.skews.withoutDeg
      };
    } else {
      this.progressBarWidth.right = `${110 - this.progressBarValue}%`;
      this.progressBarWidth.leftSkew = this.skews.withDeg;

      if (this.daysUntilSyncNeeded === this.maxDaysWithoutSync) {
        this.progressBarWidth.left = '0%';
      } else {
        this.progressBarWidth.left = '100%';
      }
    }
  }

  /**
   * Checks if active internet connection to go online, otherwise it displays a modal to inform the user
   */
  tryGoOnline() {
    if (!this.appStateService.state.isInternetConnected) {
      this.offlineService.displayNoInternetConnectionModal();
      return;
    }
    this.appStateService.goOnline()
      .subscribe(() => {
        // This is intentional
      }, () => this.offlineService.displayNoInternetConnectionModal());
  }
}
